import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import BottomNav from '../components/BottomNav';
import { 
  ArrowLeft, Fuel, MapPin, Phone, Clock, CheckCircle, XCircle, 
  RefreshCw, Filter, Loader2, Navigation
} from 'lucide-react';
import axios from 'axios';
import FuelStationsMap from '../components/FuelStationsMap';
import FuelFavoriteButton from '../components/FuelFavoriteButton';
import { useAuth } from '../context/AuthContext';

const distanceKm = (a, b) => {
  const R = 6371, toRad = (d) => d * Math.PI / 180;
  const dLat = toRad(b.latitude - a.lat), dLng = toRad(b.longitude - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.latitude)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

const API = process.env.REACT_APP_BACKEND_URL;

const FuelStationsPage = () => {
  const { api } = useAuth();
  const [stations, setStations] = useState([]);
  const [favorites, setFavorites] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [userPos, setUserPos] = useState(null);

  const loadStations = useCallback(async () => {
    setLoading(true);
    try {
      const [res, favRes] = await Promise.all([
        axios.get(`${API}/api/fuel/stations`),
        api().get('/fuel/favorites').catch(() => ({ data: [] })),
      ]);
      setStations(res.data);
      setFavorites(Object.fromEntries(favRes.data.map(f => [f.station_id, f])));
    } catch (err) {
      console.error('Error loading stations:', err);
    }
    setLoading(false);
  }, [api]);

  const updateFavorite = (stationId, fav) => setFavorites(prev => {
    const next = { ...prev };
    if (fav) next[stationId] = fav; else delete next[stationId];
    return next;
  });

  useEffect(() => {
    loadStations();
    navigator.geolocation?.getCurrentPosition(
      (pos) => setUserPos({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {}
    );
  }, [loadStations]);

  // Filter stations
  const withDistance = stations.map(s => ({
    ...s,
    distance: userPos && s.latitude && s.longitude ? distanceKm(userPos, s) : null,
  }));
  const filteredStations = withDistance.filter(station => {
    if (filter === 'gasoleo') return station.has_gasoleo;
    if (filter === 'gasolina') return station.has_gasolina;
    return true;
  });

  const sortedStations = [...filteredStations].sort((a, b) => {
    const aHasFuel = a.has_gasoleo || a.has_gasolina;
    const bHasFuel = b.has_gasoleo || b.has_gasolina;
    if (aHasFuel && !bHasFuel) return -1;
    if (!aHasFuel && bHasFuel) return 1;
    if (a.distance !== null && b.distance !== null) return a.distance - b.distance;
    if (a.distance !== null) return -1;
    if (b.distance !== null) return 1;
    if (a.fuel_updated_at && b.fuel_updated_at) {
      return new Date(b.fuel_updated_at) - new Date(a.fuel_updated_at);
    }
    return 0;
  });

  const openInMaps = (station) => {
    if (station.latitude && station.longitude) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${station.latitude},${station.longitude}`, '_blank');
    } else {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(station.name + ' ' + station.address)}`, '_blank');
    }
  };

  const stationsWithFuel = stations.filter(s => s.has_gasoleo || s.has_gasolina).length;
  const stationsWithGasoleo = stations.filter(s => s.has_gasoleo).length;
  const stationsWithGasolina = stations.filter(s => s.has_gasolina).length;

  return (
    <div className="min-h-screen bg-zinc-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-sonangol-red to-sonangol-orange pt-12 pb-6 px-4">
        <div className="flex items-center gap-4 mb-4">
          <Link to="/" data-testid="fuel-back-btn">
            <Button variant="ghost" size="icon" className="text-white hover:bg-white/20">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Fuel className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-white font-barlow font-bold text-2xl">Sonangol DC+</h1>
              <p className="text-white/80 text-sm">Disponibilidade de Combustível</p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mt-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
            <p className="text-white font-barlow font-bold text-2xl">{stationsWithFuel}</p>
            <p className="text-white/70 text-xs">Com Combustível</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
            <p className="text-white font-barlow font-bold text-2xl">{stationsWithGasoleo}</p>
            <p className="text-white/70 text-xs">Com Gasóleo</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
            <p className="text-white font-barlow font-bold text-2xl">{stationsWithGasolina}</p>
            <p className="text-white/70 text-xs">Com Gasolina</p>
          </div>
        </div>
      </div>

      {/* Filter & Refresh */}
      <div className="px-4 py-4 flex items-center justify-between">
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            data-testid="fuel-filter-all"
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              filter === 'all' 
                ? 'bg-sonangol-orange text-white' 
                : 'bg-white text-zinc-600 border border-zinc-200'
            }`}
          >
            Todos
          </button>
          <button
            onClick={() => setFilter('gasoleo')}
            data-testid="fuel-filter-gasoleo"
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              filter === 'gasoleo' 
                ? 'bg-amber-600 text-white' 
                : 'bg-white text-zinc-600 border border-zinc-200'
            }`}
          >
            Gasóleo
          </button>
          <button
            onClick={() => setFilter('gasolina')}
            data-testid="fuel-filter-gasolina"
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              filter === 'gasolina' 
                ? 'bg-green-600 text-white' 
                : 'bg-white text-zinc-600 border border-zinc-200'
            }`}
          >
            Gasolina
          </button>
        </div>
        <Button variant="ghost" size="icon" className="h-11 w-11" onClick={loadStations} disabled={loading} data-testid="fuel-refresh-btn">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      <div className="px-4 mb-4">
        <FuelStationsMap stations={sortedStations} userPos={userPos} />
      </div>

      {/* Stations List */}
      <div className="px-4 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-sonangol-orange" />
          </div>
        ) : sortedStations.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 text-center" data-testid="fuel-empty-state">
            <Fuel className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
            <p className="text-zinc-500">Nenhuma estação encontrada</p>
          </div>
        ) : (
          sortedStations.map((station) => {
            const hasGasoleo = station.has_gasoleo;
            const hasGasolina = station.has_gasolina;
            const hasFuel = hasGasoleo || hasGasolina;
            
            return (
              <div 
                key={station.id}
                data-testid={`fuel-station-card-${station.id}`}
                className={`bg-white rounded-2xl p-4 shadow-sm border ${
                  hasFuel ? 'border-green-200' : 'border-zinc-100'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0 pr-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-barlow font-bold text-zinc-800">{station.name}</h3>
                      {station.distance !== null && (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-sonangol-orange/10 text-sonangol-orange" data-testid={`fuel-station-distance-${station.id}`}>
                          {station.distance.toFixed(1)} km
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-zinc-500 text-sm mt-1">
                      <MapPin className="w-4 h-4" />
                      <span>{station.address}</span>
                    </div>
                    {station.phone && (
                      <div className="flex items-center gap-1 text-zinc-500 text-sm mt-0.5">
                        <Phone className="w-4 h-4" />
                        <span>{station.phone}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <FuelFavoriteButton key={favorites[station.id]?.updated_at || 'none'} station={station}
                      favorite={favorites[station.id]} api={api} onChange={updateFavorite} />
                    <button
                      onClick={() => openInMaps(station)}
                      data-testid={`fuel-station-navigate-${station.id}`}
                      aria-label="Abrir no mapa"
                      className="w-11 h-11 flex items-center justify-center bg-sonangol-orange/10 rounded-lg text-sonangol-orange hover:bg-sonangol-orange/20 transition-colors"
                    >
                      <Navigation className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Fuel Status */}
                <div className="flex gap-3 mb-3">
                  <div className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-xl ${
                    hasGasoleo ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'
                  }`}>
                    {hasGasoleo ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                    <span className="font-medium text-sm">Gasóleo</span>
                  </div>
                  <div className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-xl ${
                    hasGasolina ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'
                  }`}>
                    {hasGasolina ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                    <span className="font-medium text-sm">Gasolina</span>
                  </div>
                </div>

                {/* Update Time */}
                {station.fuel_updated_at ? (
                  <div className={`flex items-center gap-1 text-xs ${
                    station.is_status_recent ? 'text-green-600' : 'text-zinc-400'
                  }`}>
                    <Clock className="w-3 h-3" />
                    Actualizado: {new Date(station.fuel_updated_at).toLocaleString('pt-AO', {
                      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                    })}
                    {station.is_status_recent && (
                      <span className="ml-1 px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs">Recente</span>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-1 text-xs text-zinc-400">
                    <Clock className="w-3 h-3" />
                    Sem informação actualizada
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default FuelStationsPage;
